import { useQuery } from '@apollo/client';
import React, { useState } from 'react';
import _ from 'lodash';
import EmptyState from '../components/EmptyState';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import {
  UnorganizedPostsFilter,
  UnorganizedPostsOrder,
} from '../graphql/generated/graphql';
import allPostsIcon from '../icons/non-prefilled/16x16/allPosts.svg';
import archiveIcon from '../icons/non-prefilled/16x16/archive.svg';
import orderIcon from '../icons/non-prefilled/16x16/order.svg';
import postIcon from '../icons/non-prefilled/16x16/post.svg';
import templateIcon from '../icons/non-prefilled/16x16/template.svg';
import verifiedIcon from '../icons/non-prefilled/16x16/verified.svg';
import PostFilterMenu from '../post/PostFilterMenu';
import type { MetadataAttribute } from '../post/PostRow';
import PostRow from '../post/PostRow';
import PostSectionHeader from '../post/PostSectionHeader';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import PageMeta from '../utils/PageMeta';
import styles from './UnorganizedView.module.scss';
import { gql } from '../graphql/generated/gql';

const DEFAULT_FILTER = UnorganizedPostsFilter.Published;
const DEFAULT_ORDER = UnorganizedPostsOrder.RecentlyUpdated;

const METADATA_ATTRIBUTE: Record<UnorganizedPostsOrder, MetadataAttribute> = {
  [UnorganizedPostsOrder.Popularity]: 'popularity',
  [UnorganizedPostsOrder.RecentlyPublished]: 'publishedAt',
  [UnorganizedPostsOrder.RecentlyUpdated]: 'updatedAt',
  [UnorganizedPostsOrder.RecentlyViewed]: 'userViewedAt',
  [UnorganizedPostsOrder.Alphabetical]: 'updatedAt',
};

const emptyProps = (filter: UnorganizedPostsFilter) =>
  ({
    PUBLISHED: {
      title: 'No published posts here…',
      message: 'All published posts are organized.',
    },
    ARCHIVED: {
      title: 'No archived posts here…',
      message: 'All archived posts are organized.',
    },
    VERIFIED: {
      title: 'No verified posts here…',
      message: 'All verified posts are organized.',
    },
    TEMPLATE: {
      title: 'No templates here…',
      message: 'All templates are organized.',
    },
    ALL: {
      title: 'No posts here…',
      message: 'All posts are organized.',
    },
  }[filter]);

const UNORGANIZED_VIEW_QUERY = gql(`
  query UnorganizedView(
    $afterCursor: String
    $first: Int = 20
    $filter: UnorganizedPostsFilter
    $orderBy: UnorganizedPostsOrder
  ) {
    unorganized(
      first: $first
      after: $afterCursor
      filter: $filter
      orderBy: $orderBy
    ) {
      edges {
        node {
          ...PostRow_post
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      totalCount
    }
    allPostsCount: unorganizedCount(filter: ALL)
    archivedPostsCount: unorganizedCount(filter: ARCHIVED)
    publishedPostsCount: unorganizedCount(filter: PUBLISHED)
    verifiedPostsCount: unorganizedCount(filter: VERIFIED)
    templatePostsCount: unorganizedCount(filter: TEMPLATE)
  }
`);

const UnorganizedView = () => {
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER);

  const result = useQuery(UNORGANIZED_VIEW_QUERY, {
    variables: { filter, orderBy },
  });
  const fetchMore = ConnectionHelper.generateFetchMore(
    UNORGANIZED_VIEW_QUERY,
    result,
    'unorganized',
  );

  const { data } = result;

  const unorganized = data?.unorganized;

  const posts = _.compact(unorganized?.edges?.map((edge) => edge?.node));

  const counts: Record<UnorganizedPostsFilter, number> = {
    [UnorganizedPostsFilter.All]: data?.allPostsCount ?? 0,
    [UnorganizedPostsFilter.Archived]: data?.archivedPostsCount ?? 0,
    [UnorganizedPostsFilter.Published]: data?.publishedPostsCount ?? 0,
    [UnorganizedPostsFilter.Verified]: data?.verifiedPostsCount ?? 0,
    [UnorganizedPostsFilter.Template]: data?.templatePostsCount ?? 0,
  };

  return (
    <div id="content" className="no-padding-x no-padding-y">
      <PageMeta title="Unorganized Posts - Slab" />
      <div className={styles.container}>
        <PageContent>
          <PageTitle
            title="Unorganized"
            subtitle="These posts have no topics. Add a topic to make these posts easier for teammates discover and read."
          />
        </PageContent>
        <PostSectionHeader
          controls={
            <>
              <PostFilterMenu
                onChange={setFilter}
                options={{
                  [UnorganizedPostsFilter.Published]: {
                    icon: postIcon,
                    count: counts.PUBLISHED,
                    label: 'Published',
                  },
                  [UnorganizedPostsFilter.Verified]: {
                    icon: verifiedIcon,
                    count: counts.VERIFIED,
                    label: 'Verified',
                  },
                  [UnorganizedPostsFilter.Template]: {
                    icon: templateIcon,
                    count: counts.TEMPLATE,
                    label: 'Template',
                  },
                  [UnorganizedPostsFilter.Archived]: {
                    icon: archiveIcon,
                    count: counts.ARCHIVED,
                    label: 'Archived',
                  },
                  [UnorganizedPostsFilter.All]: {
                    icon: allPostsIcon,
                    count: counts.ALL,
                    label: 'All posts',
                  },
                }}
                title="Show"
                value={filter}
              />
              <PostFilterMenu
                icon={orderIcon}
                onChange={setOrderBy}
                options={{
                  [UnorganizedPostsOrder.Popularity]: { label: 'Popularity' },
                  [UnorganizedPostsOrder.RecentlyPublished]: {
                    label: 'Recently published',
                  },
                  [UnorganizedPostsOrder.RecentlyUpdated]: {
                    label: 'Recently updated',
                  },
                  [UnorganizedPostsOrder.RecentlyViewed]: {
                    label: 'Recently viewed',
                  },
                  [UnorganizedPostsOrder.Alphabetical]: {
                    label: 'Alphabetical',
                  },
                }}
                menuPlacement="bottom-end"
                title="Sort"
                value={orderBy}
              />
            </>
          }
        >
          <h3>Posts</h3>
        </PostSectionHeader>
        {unorganized && (
          <PageContent>
            {unorganized.totalCount === 0 ? (
              <EmptyState imageName="posts" {...emptyProps(filter)} />
            ) : (
              <>
                {posts.map((post) => (
                  <PostRow
                    key={post.id}
                    post={post}
                    metadataAttribute={METADATA_ATTRIBUTE[orderBy]}
                  />
                ))}
                <InfiniteScrollWaypoint fetchMore={fetchMore} />
              </>
            )}
          </PageContent>
        )}
      </div>
    </div>
  );
};

export default UnorganizedView;
